import _ from 'lodash';
import {emptyObject} from '@packages/utilities/fp/utils';
import {hashCode} from '@packages/utilities/string-utils';
import {collapseSpaces} from '@packages/systems/core/utils/TextUtils';
import {
  cssifyText,
  isValidClassName,
  getSelectorText,
  VariantDataMap,
} from '@packages/systems/style-legacy/selectors';
import {
  parseVariantKey,
  compareVariantKeysByStateAndPseudo,
} from '@packages/systems/style-legacy/variants';
import {compileLessWithSeenMixins} from '@packages/shared/LegacyStyleSystem/utils/StyleLessUtils';
import {swatchVariableName} from '@packages/systems/style-legacy/swatches';
import {
  AssetState,
  type StyleSheetImageResolverInterface,
} from '@packages/systems/designer/assets';
import {cssToPropertyList} from '@packages/systems/core/utils/CssStringUtils';
import {cssPropDetailsFromNativeProperty} from '@packages/shared/LegacyStyleSystem/utils/CssPropertyUtils';
import {
  addPreserve3dIfNeeded,
  addPreserve3dToStyleBlockIfNeeded,
  getMainSelectorChunks,
  getVariantSelectorChunks,
} from './StyleBlockUtils';
import {createStyleRuleGuid} from './StyleRuleUtils';
import {weakMemo, cacheMemo, singleMemo} from '@packages/utilities/memo';
import {makeAllImportant} from '@packages/systems/core/utils/StyleUtils';
import {removeHiddenStyleBlockTagStyling} from '@packages/systems/libraries';
import type {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import type {
  SwatchType,
  CssVariablesStateType,
  AppliedMixinKey,
} from '@packages/systems/core/utils/StyleTypes';
import * as StyleVariables from '@packages/systems/core/utils/StyleVariables';
import {type Breakpoint} from '@packages/systems/style/media-queries';
import type {BreakpointID} from '@packages/systems/style/types';
import {reportBugOnce} from '@packages/utilities/reporter';
import type {Font} from '@packages/systems/core/constants/Fonts';
import {Plugins} from '@packages/systems/wfdl';
import {initialCssVariablesState} from '../../stores/CssVariablesStore';
import {
  webflowizeStyleSheet,
  type WebflowCSSMediaRule,
  type WebflowCSSContainerQueryRule,
  type WebflowCSSRule,
  type WebflowContainers,
  type WebflowMedias,
  type BreakpointKey,
  type LayerSlot,
  ELEMENTS_LAYER,
  UNLAYERED,
  LAYER_SLOTS,
} from '@packages/systems/designer/styles';
import {StyleBlock} from '@packages/systems/style-legacy/records';
import {prependContainerName} from '@packages/systems/plugins/Frame';
import {
  isModeDataAttributeSelector,
  parseCollectionIdFromPropertyName,
  renderModeDataAttributeName,
  renderModeDataAttributeValue,
} from '@packages/systems/core/utils/StyleVariableModesCssUtils';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {rewriteViewportUnits} from '@packages/systems/open-canvas/rewrite-viewport-units';
import type {OpenCanvasStyleSheetOptions} from '@packages/systems/open-canvas/core';

const memoizeFunctionWithSingleStringArgument = cacheMemo(1000);

/**
 * A hacky way to detect Safari, since it has funky CSSOM bugs we have to work around.
 */
const IS_SAFARI =
  Object.prototype.toString.call(window.HTMLElement).indexOf('Constructor') > 0;

// Used to hold all global CSS variables
const ROOT_BLOCK_SELECTOR = ':root';
export const OPEN_CANVAS_FRAME_VARIABLE_BLOCK_SELECTOR =
  ':where([data-wf-frame-id] > *)';

function isBodyTagRule(masterRule: StyleRule) {
  return masterRule.type === 'tag' && masterRule.name === 'body';
}

function filterOnlyTypographicStyles(styleText: string): string {
  const typographicPropertiesList = cssToPropertyList(styleText).filter(
    ({property}: {property: string}) => {
      return Boolean(cssPropDetailsFromNativeProperty(property)?.typographic);
    }
  );

  return typographicPropertiesList
    .map(({property, value}: {property: string; value: string}) => {
      return `${property}: ${value};`;
    })
    .join(' ');
}

/**
 * On Open Canvas (Multi-Context Canvas and Component Canvas), viewport-unit
 * tokens (`Nvh`/`Nvw`/`Nvmin`/`Nvmax`/`Nvi`/`Nvb` and the s/d/l variants) must resolve
 * against the current frame, not the iframe viewport. The shared rewriter
 * replaces each token with `calc(var(--__wf_oc_*) * N)`; the `--__wf_oc_*`
 * custom properties are declared on every frame's children by `PrimitiveFrame`.
 *
 * On the legacy single-frame canvas (`isOpenCanvasActive` false/undefined) we
 * pass `styleText` through verbatim so viewport units continue to resolve
 * against the iframe viewport — pre-existing behavior.
 */
function maybeRewriteViewportUnits(
  styleText: string,
  isOpenCanvasActive: boolean
): string {
  if (!isOpenCanvasActive) return styleText;
  return rewriteViewportUnits(styleText, 'declarationList');
}

/**
 * In component canvas, we scope default-body styles to frame wrapper element. To do this while maintaining
 * the same type specificity of `body` styles in the regular canvas, we are targeting the frame using a `div` tag
 * selector + a :where() clause with the frame attribute (which does not add specificity).
 */
function getSelectorForOpenCanvas(
  masterRule: StyleRule,
  openCanvasStyleSheetOptions: OpenCanvasStyleSheetOptions | null = {}
) {
  if (
    openCanvasStyleSheetOptions?.bodyElementStyleTarget &&
    isBodyTagRule(masterRule)
  ) {
    return openCanvasStyleSheetOptions.bodyElementStyleTarget;
  }
  return masterRule.selectorText;
}

/**
 * Creates a fresh in-memory stylesheet.
 * @param  {Document}       doc   The HTML document context, typically the canvas iframe's document.
 * @return {CSSStyleSheet}        The newly created stylesheet.
 */
export function createStyleSheet(doc: Document) {
  const styleNode: HTMLStyleElement = doc.createElement('style');
  styleNode.type = 'text/css';
  // @ts-expect-error - TS2339 - Property 'rel' does not exist on type 'HTMLStyleElement'.
  styleNode.rel = 'stylesheet';

  // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
  doc.getElementsByTagName('head')[0].appendChild(styleNode);

  return webflowizeStyleSheet(doc, styleNode);
}

/**
 * Inserts a query rule (@media or @container) into the native stylesheet at the specified index.
 * @param  {CSSStyleSheet}  options.nativeSheet   The native CSS style sheet object.
 * @param  {CSSGroupingRule}  options.parentRule   The parent rule to insert the query into.
 * @param  {String}         options.ruleText      The complete CSS rule text (e.g. "@media screen and (min-width: 768px) { }" or "@container wf-container-medium (width > 0) {}").
 * @param  {String}         options.queryType     The type of query: "media" or "container".
 * @param  {Number}         options.index         (Optional) An explicit index.
 * @param  {String}         options.guid          (Optional) A guid to identify this breakpoint for Webflow.
 * @return {WebflowCSSMediaRule | WebflowCSSContainerQueryRule} The new (empty) media or container query.
 */
function insertCSSConditionalRule({
  nativeSheet,
  parentRule: parentRule_,
  ruleText,
  queryType,
  index,
  guid,
}: {
  nativeSheet: CSSStyleSheet;
  parentRule?: CSSGroupingRule | null;
  ruleText: string;
  queryType: 'media' | 'container';
  index?: number;
  guid?: string;
}): WebflowCSSMediaRule | WebflowCSSContainerQueryRule {
  const parentRule = parentRule_ ?? nativeSheet;

  // Insert the query at the specified index
  const newIndex: number = parentRule.insertRule(
    ruleText,
    index === undefined ? parentRule.cssRules.length : index
  );

  // Get the native query rule
  const nativeQuery = parentRule.cssRules[newIndex] as
    | WebflowCSSMediaRule
    | WebflowCSSContainerQueryRule;

  // Safari hack for media queries - need to remove the temporary CSS selector we added to get Safari
  // to accept the new media query...
  if (queryType === 'media' && IS_SAFARI) {
    const tempRule = _.head((nativeQuery as WebflowCSSMediaRule).cssRules);
    // @ts-expect-error - TS18048 - 'tempRule' is possibly 'undefined'.
    if (tempRule.selectorText === '.safari---temp') {
      (nativeQuery as WebflowCSSMediaRule).deleteRule(0);
    }
  }

  // Attach guid as an expando property to link this back to Webflow breakpoints
  if (guid) {
    nativeQuery.__wf_guid = guid;
    nativeQuery.__wf_rules = {};

    // IMPORTANT: Keep a reference to this native conditional query from the root sheet to prevent Chrome from garbage-collecting it
    // @ts-expect-error - TS2339 - Property '__wf_conditionals' does not exist on type 'CSSStyleSheet'.
    if (nativeSheet.__wf_conditionals) {
      const __wf_conditionals: WebflowMedias | WebflowContainers =
        // @ts-expect-error - TS2339 - Property '__wf_conditionals' does not exist on type 'CSSStyleSheet'.
        nativeSheet.__wf_conditionals;
      __wf_conditionals[guid] = nativeQuery as
        | WebflowCSSMediaRule
        | WebflowCSSContainerQueryRule;
    }
  }

  return nativeQuery;
}

/**
 * Creates a new in-memory media rule.
 * @param  {CSSStyleSheet}  options.nativeSheet   The native CSS style sheet object.
 * @param  {String}         options.mediaText     The CSS text for the media query.
 * @param  {Number}         options.index         (Optional) An explicit index.
 * @param  {String}         options.guid          (Optional) A guid to identify this breakpoint for Webflow.
 * @return {WebflowCSSMediaRule}                         The new (empty) media query rule.
 */
function createMediaQuery({
  nativeSheet,
  parentRule,
  mediaText,
  index,
  guid,
}: {
  nativeSheet: CSSStyleSheet;
  parentRule?: CSSGroupingRule | null;
  mediaText: string;
  index?: number;
  guid?: BreakpointKey;
}): WebflowCSSMediaRule {
  // Process media query text - trim, collapse spaces, and lowercase
  const processedMediaText = _.trim(collapseSpaces(mediaText.toLowerCase()));

  // Build the complete media query rule text
  const ruleText = `@media ${processedMediaText} { ${IS_SAFARI ? '.safari---temp {}' : ''} }`;

  return insertCSSConditionalRule({
    nativeSheet,
    parentRule,
    ruleText,
    queryType: 'media',
    index,
    guid,
  }) as WebflowCSSMediaRule;
}

/**
 * Creates a new in-memory container query rule.
 * @param  {CSSStyleSheet}  nativeSheet     The native CSS style sheet object.
 * @param  {CSSGroupingRule | null}  parentRule    The parent rule to insert the query into.
 * @param  {String}         containerName   The name of the container (e.g. "wf-container-medium")
 * @param  {Number}         options.index   (Optional) An explicit index.
 * @param  {String}         options.guid    (Optional) A guid to identify this breakpoint for Webflow.
 * @return {WebflowCSSContainerQueryRule}   The new container query rule.
 */
function createContainerQuery({
  nativeSheet,
  parentRule,
  containerName,
  index,
  guid,
}: {
  nativeSheet: CSSStyleSheet;
  parentRule?: CSSGroupingRule | null;
  containerName: string;
  index?: number;
  guid?: string;
}): WebflowCSSContainerQueryRule {
  // Build the complete container query rule text
  const ruleText = `@container ${containerName} (width > 0) {}`;

  return insertCSSConditionalRule({
    nativeSheet,
    parentRule,
    ruleText,
    queryType: 'container',
    index,
    guid,
  }) as WebflowCSSContainerQueryRule;
}

/**
 * Creates a new in-memory CSSStyleRule object.
 * @param  {WebflowCSSMediaRule} options.query      The native CSS query to add the rule to.
 * @param  {String}       options.selectorText    The selector text (e.g. ".button.big")
 * @param  {String}       options.styleText       The (optional) style declaration text (e.g. "border: 0; font-size: 10px;")
 * @param  {Number}       options.index           The (optional) index at which the rule should be inserted. If omitted, it is added last.
 * @param  {String}       options.guid            The (optional) guid that indentifies which StyleBlock+variant this rule comes from, e.g. "3403e81d-b374-b6a9-3226_main_hover"
 * @return {CssRule}                              The new native style rule.
 */
function createStyleRule({
  query,
  selectorText,
  styleText = '',
  seenMixins,
  index,
  guid,
  isElementStyle = false,
}: {
  query: WebflowCSSMediaRule | WebflowCSSContainerQueryRule;
  selectorText?: string | null | undefined;
  styleText?: string;
  seenMixins: Set<AppliedMixinKey>;
  index?: number;
  guid?: string;
  isElementStyle?: boolean;
}) {
  if (!isValidClassName(selectorText)) {
    throw new Error('Invalid selectorText!');
  }

  if (isElementStyle) {
    styleText = makeAllImportant(styleText);
  }

  // The above check guarantees that `cssifiedSelectorText` is a string.
  //
  // It would make more sense to do this check inside `cssifyText`, and wrap
  // these with a try/catch, or a Maybe.
  //
  // @ts-expect-error - TS2322 - Type 'string | null' is not assignable to type 'string'. | TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'.
  const cssifiedSelectorText: string = cssifyText(selectorText);
  validateStyleText(styleText);

  index = index === undefined ? query.cssRules.length : index;
  const fullText = cssifiedSelectorText + ' { ' + styleText + ' }';

  // Insert the actual rule
  // insertRule actually returns a `number`: https://github.com/facebook/flow/issues/2696
  // @ts-expect-error - TS2322 - Type 'void' is not assignable to type 'number'.
  const newIndex: number = query.insertRule(fullText, index);
  const newRule = query.cssRules[newIndex];

  // If a guid is specified, store expando properties to aid with syncronization later
  if (guid) {
    // @ts-expect-error - TS18048 - 'newRule' is possibly 'undefined'.
    newRule.__wf_guid = guid;

    // IMPORTANT: Keep a reference to this native rule from the parent media query to prevent Chrome from garbage-collecting it
    if (query.__wf_rules) {
      // @ts-expect-error - TS2322 - Type 'WebflowCSSRule | undefined' is not assignable to type 'WebflowCSSRule'.
      query.__wf_rules[guid] = newRule;
    }
  }

  // Since styleText ordering can vary when read from CSSOM (particularly the order of props), store a hash of the values used at creation to see if content has changed
  // @ts-expect-error - TS18048 - 'newRule' is possibly 'undefined'.
  newRule.__wf_hash = hashCode(styleText);

  // @ts-expect-error - TS18048 - 'newRule' is possibly 'undefined'.
  newRule.__wf_mixins = seenMixins;

  return {
    rule: newRule,
    index: newIndex,
  };
}

const NO_CHANGE /*     */ = 0b00;
const TEXT_CHANGED /*  */ = 0b01;
const STYLE_CHANGED /* */ = 0b10;
const BOTH_CHANGED /*  */ = 0b11;
/**
 * Updates the selector text and/or style text of a native CSSOM style rule.
 * @param  {CssRule} cssRule              The native style rule.
 * @param  {String} options.selectorText  The (optional) new selector text, e.g. ".button.big"
 * @param  {String} options.styleText     The new inner style text. It will only be changed if it differs from the previous value.
 * @return {Object}                       Information about whether anything was changed, e.g. { textChanged: true, styleChanged: false }
 */
export function updateStyleRule(
  cssRule: WebflowCSSRule,
  {
    selectorText,
    styleText,
    seenMixins,
    isElementStyle = false,
  }: {
    selectorText?: string | null | undefined;
    styleText?: string;
    seenMixins?: Set<AppliedMixinKey>;
    isElementStyle?: boolean;
  } = emptyObject
) {
  if (!cssRule) {
    throw new Error('Missing CSS rule!');
  }
  // if (cssRule.constructor.name !== 'CSSStyleRule')  { throw new Error('Invalid CSSStyleRule!'); }

  let result = NO_CHANGE;

  if (selectorText != null) {
    const cssifiedSelectorText = cssifyText(selectorText);

    if (cssifiedSelectorText !== cssRule.selectorText) {
      if (!cssifiedSelectorText) {
        throw new Error(
          `Invalid selector text: "${String(cssifiedSelectorText)}"`
        );
      }

      cssRule.selectorText = cssifiedSelectorText;
      result |= TEXT_CHANGED;
    }
  }

  if (isElementStyle && styleText) {
    styleText = makeAllImportant(styleText);
  }

  const newHashCode = styleText === undefined ? null : hashCode(styleText);
  if (styleText !== undefined && cssRule.__wf_hash !== newHashCode) {
    cssRule.style.cssText = styleText;
    cssRule.__wf_hash = newHashCode;
    result |= STYLE_CHANGED;
  }

  cssRule.__wf_mixins = seenMixins ?? new Set();

  return result;
}

/**
 * Inserts a ":root" block to hold global CSS variables, if it doesn't already exist.
 * @param  {CSSStyleSheet} nativeSheet  The native CSSOM stylesheet.
 * @return {CssRule}                    The CSS rule representing the :root block.
 */
function ensureRootVariableBlock(nativeSheet: CSSStyleSheet): WebflowCSSRule {
  const firstRule = nativeSheet.cssRules[0];
  // @ts-expect-error - TS2339 - Property 'selectorText' does not exist on type 'CSSRule'.
  if (firstRule && firstRule.selectorText === ROOT_BLOCK_SELECTOR) {
    return firstRule as WebflowCSSRule;
  }

  // Insert first, before any @media rules to make sure that variables inside are accessible to all later rules.
  const createdIndex: number = nativeSheet.insertRule(
    ROOT_BLOCK_SELECTOR + ' {}',
    0
  );
  const createdRule = nativeSheet.cssRules[createdIndex];

  return createdRule as WebflowCSSRule;
}

/**
 * Allows us to add variables to separate variable block for Open Canvas frames in the designer stylesheet.
 * Frames have two blocks, one in the stylesheet and one in a separate `<style>` element.
 */
function ensureOpenCanvasFrameVariableBlock(
  nativeSheet: CSSStyleSheet
): WebflowCSSRule {
  const existingRule: WebflowCSSRule | undefined = [
    ...nativeSheet.cssRules,
  ].find(
    (cssRule): cssRule is WebflowCSSRule =>
      cssRule.constructor.name === 'CSSStyleRule' &&
      (cssRule as CSSStyleRule).selectorText ===
        OPEN_CANVAS_FRAME_VARIABLE_BLOCK_SELECTOR
  );

  if (existingRule) {
    return existingRule;
  }

  const createdIndex: number = nativeSheet.insertRule(
    OPEN_CANVAS_FRAME_VARIABLE_BLOCK_SELECTOR + ' {}',
    nativeSheet.cssRules.length
  );
  const createdRule = nativeSheet.cssRules[createdIndex];

  return createdRule as WebflowCSSRule;
}

function setCssVariable(
  rule: WebflowCSSRule,
  name: string,
  value: string,
  priority: 'important' | '' = ''
) {
  rule.style.setProperty(`--${name}`, value, priority);
}

/**
 * Rewrites a map of CSS variable values only when Open Canvas is active and
 * at least one value contains viewport units that actually change after
 * rewriting. Otherwise returns the original map reference.
 */
function rewriteValueMapIfNeeded(
  values: Map<string, string>,
  isOpenCanvasActive: boolean
) {
  if (!isOpenCanvasActive) {
    return values;
  }

  let rewrittenValues: Map<string, string> | null = null;
  for (const [name, value] of values) {
    const rewrittenValue = rewriteViewportUnits(value, 'value');
    if (rewrittenValue !== value) {
      rewrittenValues ??= new Map(values);
      rewrittenValues.set(name, rewrittenValue);
    }
  }

  return rewrittenValues ?? values;
}

function removeCssVariable(rule: WebflowCSSRule, name: string) {
  rule.style.removeProperty(`--${name}`);
}

/**
 * Ensures a CSS rule exists in the designer stylesheet for a given variable
 * mode's data-attribute selector (e.g. `[data-wf-mode--theme="dark"]`).
 * If the rule doesn't exist yet, it's created and populated with the
 * collection's variable definitions for that mode. Returns the rule, or
 * null if the collection or mode can't be resolved.
 */
function ensureModePropDataAttributeBlock(
  nativeSheet: CSSStyleSheet,
  variableValueMap: VariableValueMap,
  collectionId: StyleVariables.VariableCollectionId,
  variableCollections: StyleVariables.VariableCollectionMap,
  modeId: StyleVariables.VariableModeId,
  slugMap: Plugins.PluginsType['slugMap'],
  isOpenCanvasActive: boolean
): WebflowCSSRule | null {
  const collection = variableCollections.get(collectionId);
  if (!collection) {
    return null;
  }
  const modeDataAttributeName = renderModeDataAttributeName(
    collection,
    slugMap
  );
  let modeName;
  if (modeId === StyleVariables.INHERIT_VARIABLE_MODE_ID) {
    modeName = 'inherit';
  } else if (modeId === StyleVariables.BASE_VARIABLE_MODE_ID) {
    modeName = 'base';
  } else {
    modeName = collection.modes[modeId]?.name;
  }
  if (!modeName) {
    return null;
  }
  const selectorText = `[${modeDataAttributeName}="${renderModeDataAttributeValue(modeName)}"]`;

  const existingRule: WebflowCSSRule | undefined = [
    ...nativeSheet.cssRules,
  ].find(
    (cssRule): cssRule is WebflowCSSRule =>
      cssRule.constructor.name === 'CSSStyleRule' &&
      (cssRule as CSSStyleRule).selectorText === selectorText
  );

  if (existingRule) {
    return existingRule;
  }

  const createdIndex: number = nativeSheet.insertRule(
    selectorText + ' {}',
    nativeSheet.cssRules.length
  );
  const createdRule = nativeSheet.cssRules[createdIndex] as WebflowCSSRule;

  const values =
    variableValueMap.get(collectionId)?.get(modeId) ??
    new Map<string, string>();

  for (const [name, value] of values) {
    const finalValue = isOpenCanvasActive
      ? rewriteViewportUnits(value, 'value')
      : value;
    setCssVariable(
      createdRule,
      name,
      finalValue,
      // For now, we use `!important` to ensure mode props "win"; in a future
      // implementation we may harness a separate `@layer` or use the inherent
      // `@layer` system from element styles to accomplish this:
      'important'
    );
  }

  return createdRule as WebflowCSSRule;
}

/**
 * Sets a global CSS variable in the :root block via CSSOM.
 * @param {CssStyleSheet} nativeSheet The native CSSOM stylesheet.
 * @param {String}        name        The variable name not including the "--" prefix, e.g. "swatch_12345678"
 * @param {String}        value       The variable value, e.g. "red"
 * If a caller writes user-authored CSS values (anything that could contain
 * viewport units), pass `isOpenCanvasActive: true` to route through the
 * viewport-unit rewriter.
 */
function setRootCssVariable(
  nativeSheet: CSSStyleSheet,
  name: string,
  value: string,
  isOpenCanvasActive: boolean = false
) {
  const rootBlock = ensureRootVariableBlock(nativeSheet);
  const finalValue = isOpenCanvasActive
    ? rewriteViewportUnits(value, 'value')
    : value;
  setCssVariable(rootBlock, name, finalValue, '');
}

/**
 * Removes a CSS variable from the :root block via CSSOM.
 * @param {CssStyleSheet} nativeSheet The native CSSOM stylesheet.
 * @param {String}        name        The variable name to remove, not including the "--" prefix, e.g. "swatch_12345678"
 */
function removeRootCssVariable(nativeSheet: CSSStyleSheet, name: string) {
  const rootBlock = ensureRootVariableBlock(nativeSheet);
  removeCssVariable(rootBlock, name);
}

function getBreakpointKey(
  layerSlot: LayerSlot,
  breakpointId: BreakpointID
): BreakpointKey {
  return layerSlot === UNLAYERED
    ? breakpointId
    : `${layerSlot}--${breakpointId}`;
}

function ensureLayerBlockRule({
  nativeSheet,
  layerName,
}: {
  nativeSheet: CSSStyleSheet;
  layerName: string;
}) {
  const existingRule = [...nativeSheet.cssRules].find(
    (cssRule): cssRule is CSSLayerBlockRule =>
      cssRule.constructor.name === 'CSSLayerBlockRule' &&
      (cssRule as CSSLayerBlockRule).name === layerName
  );
  if (existingRule) {
    return existingRule;
  }

  const ruleIndex = nativeSheet.insertRule(
    `@layer ${layerName} {}`,
    nativeSheet.cssRules.length
  );
  return nativeSheet.cssRules[ruleIndex] as CSSLayerBlockRule;
}

/**
 * Makes sure that each media query exists in the style sheet as either a media query or a container query.
 * For now, it assumes that once a media query is created, its mediaText never has to change (until we allow media query ranges to be adjusted).
 * @param  {CSSStyleSheet}  nativeSheet  The native CSSOM stylesheet.
 * @param  {OrderedMap}     breakpoints The map of all current breakpoints.
 * @param  {Boolean}        isOpenCanvasActive Whether we are in component canvas mode (uses container queries instead of media queries)
 * @return {String[]}                    The array of MQ keys that were added.
 */
function ensureMediaQueries(
  nativeSheet: CSSStyleSheet,
  breakpointsMap: StyleBlockStateType['breakpoints'],
  isOpenCanvasActive?: boolean,
  isElementStylesEnabled?: boolean
) {
  // Get existing queries based on mode
  const existingQueries = isOpenCanvasActive
    ? getNativeContainerQueriesForOpenCanvas(nativeSheet)
    : getNativeMediaRules(nativeSheet);

  const existingKeys = new Set(existingQueries.map((rule) => rule.__wf_guid));

  const addedBreakpointIds: Array<BreakpointID> = [];
  const breakpoints: Breakpoint[] = Object.values(breakpointsMap) as any;
  const breakpointsWithParentRule: Array<{
    parentRule: CSSGroupingRule | null;
    breakpoint: Breakpoint;
    breakpointKey: BreakpointKey;
  }> = [];

  const slots: readonly LayerSlot[] = isElementStylesEnabled
    ? LAYER_SLOTS
    : [UNLAYERED];

  for (const slot of slots) {
    const parentRule =
      typeof slot === 'string'
        ? ensureLayerBlockRule({nativeSheet, layerName: slot})
        : null;
    for (const breakpoint of breakpoints) {
      breakpointsWithParentRule.push({
        parentRule,
        breakpoint,
        breakpointKey: getBreakpointKey(slot, breakpoint.id),
      });
    }
  }

  for (const {
    parentRule,
    breakpoint,
    breakpointKey,
  } of breakpointsWithParentRule) {
    const guid = isOpenCanvasActive
      ? prependContainerName(breakpointKey)
      : breakpointKey;

    // Already have the correct query type, skip
    if (existingKeys.has(guid)) {
      // TODO: Update query text if ranges have changed
      continue;
    }

    // Create the appropriate query type based on mode
    // As of now, there should only be @media or @container, never both
    if (isOpenCanvasActive) {
      // Component Canvas mode - transform media queries to container queries
      // i.e. `@container wf-container-medium (width > 0) {}`

      // Our container _name_ must just use the breakpoint ID since we only have
      // a single container even for other layers:
      const containerName = prependContainerName(breakpoint.id);
      createContainerQuery({
        nativeSheet,
        parentRule,
        containerName,
        guid,
      });
    } else {
      // Normal designer canvas mode - use media queries
      const mediaText =
        breakpoint.id === 'main'
          ? 'all'
          : // TODO remove this once we refactor `createMediaQuery()`
            // https://github.com/webflow/webflow/issues/33342
            breakpoint.queries.map((query) => query.toCSS()).join(', ');
      // TODO CustomBreakpoints: When arbitrary MQs are allowed, need to make sure the insertion happens at the correct index instead of defaulting to the end
      createMediaQuery({
        nativeSheet,
        parentRule,
        mediaText,
        guid: breakpointKey,
      });
    }

    addedBreakpointIds.push(breakpoint.id);
  }

  return addedBreakpointIds;
}

const CSS_CONTAINER_RULE_NAME = 'CSSContainerRule';
const CSS_MEDIA_RULE_NAME = 'CSSMediaRule';

/**
 * Returns just the top-level native media query rules, without any other top-level rules (e.g. :root block for CSS variables, etc).
 * @param  {CSSStyleSheet}  nativeSheet  The native CSSOM stylesheet.
 * @return {WebflowCSSMediaRule[]}              An array of native media rules.
 */
function getNativeMediaRules(
  nativeSheet: CSSStyleSheet
): WebflowCSSMediaRule[] {
  return Object.values(
    (nativeSheet as any).__wf_conditionals as WebflowMedias | WebflowContainers
  ).filter(
    (
      rule: WebflowCSSMediaRule | WebflowCSSContainerQueryRule
    ): rule is WebflowCSSMediaRule =>
      rule.constructor.name === CSS_MEDIA_RULE_NAME
  );
}

/**
 * Returns only container query rules for open canvas (i.e. with wf-container prefix).
 * @param  {CSSStyleSheet}  nativeSheet                  The native CSSOM stylesheet.
 * @return {WebflowCSSContainerQueryRule[]}              An array of native container query rules for open canvas (i.e. with wf-container prefix).
 */
function getNativeContainerQueriesForOpenCanvas(
  nativeSheet: CSSStyleSheet
): WebflowCSSContainerQueryRule[] {
  return Object.values(
    (nativeSheet as any).__wf_conditionals as WebflowMedias | WebflowContainers
  ).filter(
    (
      rule: WebflowCSSMediaRule | WebflowCSSContainerQueryRule
    ): rule is WebflowCSSContainerQueryRule =>
      rule.constructor.name === CSS_CONTAINER_RULE_NAME
  );
}
type StyleRule = {
  guid: string;
  selectorText: string;
  styleLess: string;
  type: string;
  name: string;
};

type StyleRuleMap = Partial<Record<BreakpointKey, StyleRule[]>>;

/**
 * Creates a map of ordered style rules for the entire style tree to later apply to the CSSOM style sheet.
 * @param  {StyleBlockStateType}  options.styleBlockState   The latest style tree state.
 * @return {Object}                                     e.g. { main: [ { sel: '.button', text: 'color: red;', hash: 12345678 } ] }
 */
const generateRuleMap = weakMemo((slugMap: Plugins.PluginsType['slugMap']) =>
  weakMemo((variantDataMap: VariantDataMap) =>
    weakMemo((styleBlockState: StyleBlockStateType): StyleRuleMap => {
      const breakpointIds = styleBlockState.getOrderedBreakpointIds();

      // `getOrderedList` returns the blocks in cascade order, resolved from the
      // tag styles settings held on the state — the same source the panel reads,
      // so the two cannot drift. See also `compileStyles` and `getLinkedRules`.
      const orderedStyleBlockList = styleBlockState.getOrderedList({
        slugMap,
        decorate: false,
      });
      const ruleMap: StyleRuleMap = {};
      for (const layer of LAYER_SLOTS) {
        for (const breakpointId of breakpointIds) {
          const breakpointKey = getBreakpointKey(layer, breakpointId);
          ruleMap[breakpointKey] = [];
        }
      }

      // We make 1 pass over the entire style block list per media query, collecting relevant CSS selectors for each breakpoint
      for (const orderedStyleBlock of orderedStyleBlockList) {
        const blockGuid = orderedStyleBlock._id;

        // An inactive library's tag style keeps mode applications, not styling.
        let styleBlock = orderedStyleBlock;
        if (styleBlockState.isHiddenLibraryTagStyle(blockGuid)) {
          const reduced = removeHiddenStyleBlockTagStyling(styleBlock);
          if (reduced == null) {
            continue;
          }
          styleBlock = reduced;
        }

        const treePath = styleBlockState.getTreePath(blockGuid);
        const mainSelectorChunks = getMainSelectorChunks(treePath);

        const variants = styleBlock.variants;

        const variantKeysByBreakpointId: Partial<
          Record<BreakpointID, string[]>
        > = {};
        if (variants.size > 0) {
          for (const variantKey of variants.keys()) {
            const {mediaQuery} = parseVariantKey(variantKey);

            if (!variantKeysByBreakpointId[mediaQuery]) {
              variantKeysByBreakpointId[mediaQuery] = [];
            }

            variantKeysByBreakpointId[mediaQuery].push(variantKey);
          }
        }

        for (const breakpointId of breakpointIds) {
          const breakpointKey = getBreakpointKey(
            styleBlock.type === 'element' ? ELEMENTS_LAYER : UNLAYERED,
            breakpointId
          );
          const rules = ruleMap[breakpointKey];

          let selectorText = getSelectorText(
            mainSelectorChunks,
            slugMap,
            variantDataMap
          );

          if (selectorText != null && breakpointId === 'main') {
            // @ts-expect-error - TS18048 - 'rules' is possibly 'undefined'.
            rules.push({
              guid: blockGuid + ':main',
              selectorText,
              styleLess: (styleBlock.get('styleLess') || '').trim(),
              type: styleBlock.type,
              name: styleBlock.name,
            });
          }

          if (variantKeysByBreakpointId[breakpointId]) {
            const relevantVariantKeys = variantKeysByBreakpointId[breakpointId];
            relevantVariantKeys.sort(compareVariantKeysByStateAndPseudo);

            for (const variantKey of relevantVariantKeys) {
              // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'BreakpointID'.
              const variant = variants.get(variantKey);
              if (variant) {
                const variantSelectorChunks = getVariantSelectorChunks(
                  variantKey,
                  mainSelectorChunks
                );

                selectorText = getSelectorText(
                  variantSelectorChunks,
                  slugMap,
                  variantDataMap
                );

                if (selectorText != null) {
                  // @ts-expect-error - TS18048 - 'rules' is possibly 'undefined'.
                  rules.push({
                    guid: createStyleRuleGuid(blockGuid, variantKey),
                    selectorText,
                    styleLess: (variant.get('styleLess') || '').trim(),
                    type: styleBlock.type,
                    name: styleBlock.name,
                  });
                }
              }
            }
          }
        }
      }

      return ruleMap;
    })
  )
);

const getMemoizedLessCompiler = weakMemo(
  (slugMap: Plugins.PluginsType['slugMap']) =>
    weakMemo((styleSheetImageResolver: StyleSheetImageResolverInterface) =>
      weakMemo((cssVariablesState: CssVariablesStateType) =>
        memoizeFunctionWithSingleStringArgument((less: string) => {
          return compileLessWithSeenMixins(less, {
            slugMap,
            styleSheetImageResolver,
            cssVariablesState,
          });
        })
      )
    )
);

const DEFAULT_ASSET_STATE = new AssetState();

/**
 * Calculates differences between the StyleBlockStore and the CSSOM style sheet and applies operations to make sure they are synced up.
 * @param  {CssStyleSheet}    nativeSheet             The native CSSOM stylesheet.
 * @param  {StyleBlockStateType}  options.styleBlockState The current state to match.
 * @param  {StyleSheetImageResolverInterface} options.styleSheetImageResolver  Resolves @img_X placeholders to full URLs.
 * @param  {Boolean}          options.audit           If true, will record an audit trail of actions (used only for testing).
 * @return {Object[]}                                 If audit = true, the list of operations applied. Otherwise an empty array.
 */
export function syncStyleSheet(
  nativeSheet: CSSStyleSheet,
  {
    styleBlockState,
    slugMap,
    variantDataMap,
    styleSheetImageResolver = DEFAULT_ASSET_STATE,
    cssVariablesState = initialCssVariablesState,
    audit = false,
    isElementStylesEnabled = false,
    isOpenCanvasActive = false,
    openCanvasStyleSheetOptions = null,
    wasOpenCanvasActive = false,
  }: {
    styleBlockState: StyleBlockStateType;
    slugMap: Plugins.PluginsType['slugMap'];
    variantDataMap: VariantDataMap;
    styleSheetImageResolver?: StyleSheetImageResolverInterface;
    cssVariablesState?: CssVariablesStateType;
    audit?: boolean;
    isElementStylesEnabled?: boolean;
    isOpenCanvasActive?: boolean;
    openCanvasStyleSheetOptions?: OpenCanvasStyleSheetOptions | null;
    wasOpenCanvasActive?: boolean;
  }
) {
  function getConditionalRules() {
    return isOpenCanvasActive
      ? getNativeContainerQueriesForOpenCanvas(nativeSheet)
      : getNativeMediaRules(nativeSheet);
  }

  function getConditionalRule(
    breakpointId: BreakpointID,
    styleBlock: StyleBlock
  ) {
    const baseStylesBreakpointKey = isElementStylesEnabled
      ? getBreakpointKey(UNLAYERED, breakpointId as BreakpointID)
      : breakpointId;
    const elementStylesBreakpointKey = getBreakpointKey(
      ELEMENTS_LAYER,
      breakpointId as BreakpointID
    );
    const queryKey = isOpenCanvasActive
      ? prependContainerName(baseStylesBreakpointKey)
      : baseStylesBreakpointKey;
    const elementStylesQueryKey = isOpenCanvasActive
      ? prependContainerName(elementStylesBreakpointKey)
      : elementStylesBreakpointKey;

    return getConditionalRules().find((mediaRule) =>
      styleBlock.type === 'element'
        ? mediaRule.__wf_guid === elementStylesQueryKey
        : mediaRule.__wf_guid === queryKey
    );
  }

  /**
   * When switching between container query mode (component canvas) and media
   * query mode (regular designer canvas), we need to clean up the queries we
   * don't want based on the new mode.
   *
   * - In container query mode (component canvas), remove any media queries.
   * - In media query mode (regular designer canvas), remove any container queries.
   */
  if (wasOpenCanvasActive !== isOpenCanvasActive) {
    const nativeRules = isOpenCanvasActive
      ? getNativeMediaRules(nativeSheet)
      : getNativeContainerQueriesForOpenCanvas(nativeSheet);
    // (Delete rules in reverse order to avoid index shifting)
    for (let i = nativeRules.length - 1; i >= 0; i--) {
      const rule = nativeRules[i];
      if (rule) {
        const parentRule =
          (rule.parentRule as CSSGroupingRule | null) ?? nativeSheet;
        const index = Array.from(parentRule.cssRules).indexOf(rule as CSSRule);
        if (index !== -1) {
          parentRule.deleteRule(index);
        }
      }
    }
    // Clear the __wf_conditionals map
    // @ts-expect-error - TS2339 - Property '__wf_conditionals' does not exist on type 'CSSStyleSheet'.
    nativeSheet.__wf_conditionals = {};
  }

  const auditActions = audit ? [] : null;

  const compileLessMemoized = getMemoizedLessCompiler(slugMap)(
    styleSheetImageResolver
  )(cssVariablesState);

  // Detect whether the state has changed just by changing just one StyleBlock's styleLess value
  // If that is detected, we should only update that single rule's inner CSS text
  const changedRuleGuid = styleBlockState.changedRule.get('guid');
  if (changedRuleGuid) {
    const sync = (id: string): undefined | typeof auditActions => {
      const [changedBlockId, variantKey] = id.split(':');
      const changedBlock = styleBlockState.getBlock(changedBlockId);
      if (changedBlock == null) {
        return auditActions;
      }
      // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
      const variantParts = parseVariantKey(variantKey);
      const masterBlock = addPreserve3dToStyleBlockIfNeeded(changedBlock);
      const variantPath = variantKey === 'main' ? [] : ['variants', variantKey];
      const newStyleLess = masterBlock.getIn([
        ...variantPath,
        'styleLess',
      ]) as string;
      const {styleText: newStyleText, seenMixins: newSeenMixins} =
        masterBlock.get('ephemeral')
          ? // If we're making ephemeral changes, avoid filling the memoizer cache:
            compileLessWithSeenMixins(newStyleLess, {
              slugMap,
              styleSheetImageResolver,
            })
          : // ...Otherwise, use the memoized version:
            compileLessMemoized(newStyleLess);

      const nativeRule = _.find(
        getConditionalRule(variantParts.mediaQuery, masterBlock)?.cssRules,
        (cssRule) => cssRule.__wf_guid === id
      );
      let needsFullSync = false;
      if (nativeRule) {
        if (variantKey !== 'main') {
          const nativeMasterMediaQuery = getConditionalRule(
            'main',
            masterBlock
          );
          const nativeMasterRule = _.find(
            // @ts-expect-error - TS18048 - 'nativeMasterMediaQuery' is possibly 'undefined'. | TS2339 - Property 'cssRules' does not exist on type 'CSSRule'.
            nativeMasterMediaQuery.cssRules,
            (cssRule) => cssRule.__wf_guid === masterBlock._id + ':main'
          );

          if (!nativeMasterRule) {
            needsFullSync = true;
          } else {
            const {
              styleText: newMasterStyleText,
              seenMixins: newMasterSeenMixins,
            } = masterBlock.get('ephemeral')
              ? // If we're making ephemeral changes, avoid filling the memoizer cache:
                compileLessWithSeenMixins(masterBlock.styleLess, {
                  slugMap,
                  styleSheetImageResolver,
                })
              : // ...Otherwise, use the memoized version:
                compileLessMemoized(masterBlock.styleLess);
            updateSingleRuleWithAudit(
              nativeMasterRule,
              undefined,
              maybeRewriteViewportUnits(newMasterStyleText, isOpenCanvasActive),
              newMasterSeenMixins,
              auditActions,
              masterBlock.type === 'element'
            );
          }
        }

        if (!needsFullSync) {
          updateSingleRuleWithAudit(
            nativeRule,
            undefined,
            maybeRewriteViewportUnits(newStyleText, isOpenCanvasActive),
            newSeenMixins,
            auditActions,
            masterBlock.type === 'element'
          );

          return auditActions;
        }
      }
    };

    if (Array.isArray(changedRuleGuid)) {
      const shouldReturnAuditActions = changedRuleGuid
        .map(sync)
        .some((action) => action !== undefined);
      if (shouldReturnAuditActions) {
        return auditActions;
      }
    } else {
      const shouldReturnAuditActions = sync(changedRuleGuid) !== undefined;
      if (shouldReturnAuditActions) {
        return auditActions;
      }
    }
  }

  styleBlockState = addPreserve3dIfNeeded(styleBlockState);

  // Otherwise we need to sync the entire style sheet
  ensureMediaQueries(
    nativeSheet,
    styleBlockState.breakpoints,
    isOpenCanvasActive,
    isElementStylesEnabled
  );

  // Since CSStyleRule.cssRules does not support standard array methods (indexOf, etc), need to convert to a map
  const existingQueries = new Map(
    getConditionalRules().map((rule) => [rule.__wf_guid, rule])
  );

  // This will be the future state of the stylesheet, and the work below make sure to syncronize to it
  const masterRulesMap =
    generateRuleMap(slugMap)(variantDataMap)(styleBlockState);

  if (auditActions) {
    // @ts-expect-error - TS2345 - Argument of type '{ type: string; }' is not assignable to parameter of type 'never'.
    auditActions.push({type: 'recalc'});
  }

  const breakpointInfos: Array<{
    breakpointKey: BreakpointKey;
    breakpointId: BreakpointID;
  }> = [];
  const orderedBreakpointIds = styleBlockState.getOrderedBreakpointIds();
  const slots: readonly LayerSlot[] = isElementStylesEnabled
    ? LAYER_SLOTS
    : [UNLAYERED];
  for (const slot of slots) {
    for (const breakpointId of orderedBreakpointIds) {
      breakpointInfos.push({
        breakpointKey: getBreakpointKey(slot, breakpointId),
        breakpointId,
      });
    }
  }

  for (const {breakpointKey} of breakpointInfos) {
    // Get the appropriate query based on mode
    const queryKey = isOpenCanvasActive
      ? prependContainerName(breakpointKey)
      : breakpointKey;
    const nativeQuery = existingQueries.get(queryKey);

    if (!nativeQuery) {
      throw new Error(
        `Could not find ${isOpenCanvasActive ? 'container' : 'media'} query with key "${queryKey}"`
      );
    }

    const masterRules = masterRulesMap[breakpointKey];

    // If we detect any non-Webflow rules, this is a major error so don't even try to sync
    const unknownRules = _.filter(nativeQuery.cssRules, (rule) => {
      return !rule.__wf_guid;
    });
    if (unknownRules.length) {
      throw new Error(
        // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
        'Unrecognized dynamic CSS rule: ' + unknownRules[0].cssText
      );
    }

    // Delete rules before making add/update pass to make logic easier
    const keepGuids = new Set(
      // @ts-expect-error - TS18048 - 'masterRules' is possibly 'undefined'.
      masterRules.map((rule) =>
        isOpenCanvasActive
          ? prependContainerName(rule.guid as BreakpointKey)
          : rule.guid
      )
    );

    // Iterating through our rules list in _reverse_ ensures that we can safely
    // index into the list as we remove items:
    for (let i = nativeQuery.cssRules.length - 1; i >= 0; --i) {
      const oldRule = nativeQuery.cssRules[i];
      if (!oldRule || keepGuids.has(oldRule.__wf_guid)) {
        continue;
      }
      nativeQuery.deleteRule(i);
      if (auditActions) {
        // @ts-expect-error - TS2345 - Argument of type '{ type: string; guid: string; }' is not assignable to parameter of type 'never'.
        auditActions.push({type: 'del', guid: oldRule.__wf_guid});
      }
    }

    const updateRule = ({
      query,
      masterRule,
      index,
      isContainerQuery = false,
    }: {
      query: WebflowCSSMediaRule | WebflowCSSContainerQueryRule;
      masterRule: StyleRule;
      index: number;
      isContainerQuery?: boolean;
    }) => {
      const existingRule = query.cssRules[index];
      const masterRuleGuid = isContainerQuery
        ? prependContainerName(masterRule.guid as BreakpointID)
        : masterRule.guid;
      const selectorText = isOpenCanvasActive
        ? getSelectorForOpenCanvas(masterRule, openCanvasStyleSheetOptions)
        : masterRule.selectorText;
      const {styleText: compiledStyleText, seenMixins: compiledSeenMixins} =
        compileLessMemoized(masterRule.styleLess);
      const filteredStyleText =
        isOpenCanvasActive &&
        openCanvasStyleSheetOptions?.filterOpenCanvasBodyStyles &&
        isBodyTagRule(masterRule)
          ? filterOnlyTypographicStyles(compiledStyleText)
          : compiledStyleText;
      const styleText = maybeRewriteViewportUnits(
        filteredStyleText,
        isOpenCanvasActive
      );

      const isElementStyle = masterRule.type === 'element';

      // If the rule is already in the style sheet, update it's data
      if (existingRule && existingRule.__wf_guid === masterRuleGuid) {
        updateSingleRuleWithAudit(
          existingRule,
          selectorText,
          styleText,
          compiledSeenMixins,
          auditActions,
          isElementStyle
        );
      } else if (
        existingRule === undefined ||
        existingRule.__wf_guid !== masterRule.guid
      ) {
        // Else if there is no matching rule at the index, or another guid is in its place, need to insert a new rule
        createStyleRule({
          guid: masterRuleGuid,
          query,
          selectorText,
          styleText,
          seenMixins: compiledSeenMixins,
          index,
          isElementStyle,
        });

        if (auditActions) {
          // @ts-expect-error - TS2345 - Argument of type '{ type: string; guid: string; after: string | null; }' is not assignable to parameter of type 'never'.
          auditActions.push({
            type: 'add',
            guid: masterRule.guid,
            after: index === 0 ? null : query.cssRules[index - 1]?.__wf_guid,
          });
        }

        // If there was an existing rule, it doesn't match, so let's remove it.
        if (existingRule) {
          let wasRemoved;
          if (query.cssRules[index + 1] === existingRule) {
            query.deleteRule(index + 1);
            wasRemoved = true;
          } else {
            // We use `index + 1` above because the `createStyleRule` call
            // should insert the new rule in the place where `existingRule` was,
            // pushing it one place after `index` in the CSSRuleList.
            //
            // In the event this assumption is no longer true, then we look up
            // the rule with `indexOf`, remove it, and report this as a bug
            // since we don't expect this to happen.

            const indexToRemove = _.indexOf(query.cssRules, existingRule);
            if (indexToRemove === -1) {
              wasRemoved = false;
            } else {
              query.deleteRule(indexToRemove);
              wasRemoved = true;
            }

            reportBugOnce(
              'SyncStyleSheetExistingRuleUnexpectedIndex',
              'existingRule was not at the expected index and therefore removeStyleRule was needed',
              {
                team: 'dxp',
                ticket: 'https://webflow.atlassian.net/browse/DESXP-6152',
                wasRemoved,
              }
            );
          }

          if (auditActions && wasRemoved) {
            // @ts-expect-error - TS2345 - Argument of type '{ type: string; guid: string; }' is not assignable to parameter of type 'never'.
            auditActions.push({type: 'del', guid: existingRule.__wf_guid});
          }
        }
      }
    };

    // Walk through each master rule and either verify / update / create it
    // @ts-expect-error - TS18048 - 'masterRules' is possibly 'undefined'.
    masterRules.forEach((masterRule, index) => {
      updateRule({
        query: nativeQuery,
        masterRule,
        index,
        isContainerQuery: isOpenCanvasActive,
      });
    });
  }

  return auditActions;
}

/**
 * Syncs CssVariablesStore.swatches changes to the stylesheet via CSSOM.
 *
 * @param  {CssStyleSheet}  styleSheet  The native stylesheet
 * @param  {Object[]}       oldSwatches The old array of swatches.
 * @param  {Object[]}       newSwatches The new array of swatches.
 */
export function syncSwatchesToCssVariables(
  styleSheet: CSSStyleSheet,
  {
    oldSwatches = [],
    newSwatches = [],
  }: {
    oldSwatches?: SwatchType[];
    newSwatches?: SwatchType[];
  }
) {
  // @ts-expect-error - TS7031 - Binding element 'id' implicitly has an 'any' type. | TS7031 - Binding element 'previewColor' implicitly has an 'any' type. | TS7031 - Binding element 'color' implicitly has an 'any' type.
  const swatchToVariable = ({id, previewColor, color}) => {
    return {
      name: swatchVariableName(id),
      value: previewColor || color,
    };
  };
  const oldVariables = oldSwatches.map(swatchToVariable);
  const newVariables = newSwatches.map(swatchToVariable);
  const oldVariablesMap = _.keyBy(oldVariables, 'name');
  const newVariablesMap = _.keyBy(newVariables, 'name');

  const variablesToSet = _.reduce(
    newVariables,
    (keep, {name, value}) => {
      const oldValue = oldVariablesMap[name] || newVariablesMap[name];
      if (!oldValue || oldValue !== value) {
        // @ts-expect-error - TS2769 - No overload matches this call.
        return keep.concat([{name, value}]);
      }

      return keep;
    },
    []
  );

  variablesToSet.forEach(({name, value}) =>
    setRootCssVariable(styleSheet, name, value)
  );

  const variablesToRemove = oldVariables.filter(({name}) => {
    return !newVariablesMap[name];
  });
  variablesToRemove.forEach(({name}) =>
    removeRootCssVariable(styleSheet, name)
  );
}

/**
 * INTERNAL
 *
 * Helper reducer function. See `renderVariablesToMap`.
 */
const reduceVariablesToCssVariables =
  (
    fonts: Array<Font>,
    variableCollections: StyleVariables.VariableCollectionMap,
    slugMap: Plugins.PluginsType['slugMap']
  ) =>
  (
    tempMap: Map<
      StyleVariables.VariableCollectionId,
      Map<StyleVariables.VariableModeId, Map<string, string>>
    >,
    variable: StyleVariables.AnyVariableType,
    __: StyleVariables.VariableID,
    variables: StyleVariables.VariableMap
  ): Map<
    StyleVariables.VariableCollectionId,
    Map<StyleVariables.VariableModeId, Map<string, string>>
  > => {
    const collection = StyleVariables.getVariableCollection(
      variable,
      variableCollections
    );
    if (collection == null) {
      return tempMap;
    }
    const collectionMap: Map<string, Map<string, string>> = tempMap.get(
      collection.id
    ) ?? new Map();

    const cssVariableNameResult = StyleVariables.safeRenderCssVariableName(
      variables,
      variableCollections,
      slugMap,
      variable.id,
      false
    );
    if (cssVariableNameResult.error) {
      return tempMap;
    }
    const cssVariableName = cssVariableNameResult.result;

    // First, set the base mode value:
    {
      const renderedValue = StyleVariables.renderCssValue(
        variables,
        variableCollections,
        slugMap,
        variable.id,
        fonts
      );
      if (renderedValue instanceof Error) {
        throw renderedValue;
      }

      const baseValues: Map<string, string> =
        collectionMap.get(StyleVariables.BASE_VARIABLE_MODE_ID) ?? new Map();

      baseValues.set(cssVariableName, renderedValue);

      collectionMap.set(StyleVariables.BASE_VARIABLE_MODE_ID, baseValues);
    }

    // Then, set the mode values:
    for (const [modeId, modeValue] of Object.entries(
      // NOTE: On some legacy sites, `variable.modes` can be nullish:
      variable.modes ?? {}
    )) {
      const renderedValue = StyleVariables.renderAnyCssValue(
        modeValue,
        variables,
        variableCollections,
        slugMap,
        fonts
      );
      if (renderedValue instanceof Error) {
        throw renderedValue;
      }

      const modeValues: Map<string, string> =
        collectionMap.get(modeId) ?? new Map();
      modeValues.set(cssVariableName, renderedValue);
      collectionMap.set(modeId, modeValues);
    }

    tempMap.set(collection.id, collectionMap);

    return tempMap;
  };

type VariableValueMap = Map<
  StyleVariables.VariableCollectionId,
  Map<StyleVariables.VariableModeId, Map<string, string>>
>;

/**
 * INTERNAL
 *
 * Helper function that renders a VariableMap into a Map of CSS variable names
 * to their values.
 */
const renderVariablesToMap = singleMemo(
  (slugMap: Plugins.PluginsType['slugMap']) =>
    singleMemo((variableCollections: StyleVariables.VariableCollectionMap) =>
      singleMemo((fonts: Font[]) =>
        singleMemo(
          (variables: StyleVariables.VariableMap): VariableValueMap =>
            variables.reduce(
              // @ts-expect-error - Immutable types for Iterable are not compat with OrderedMap?
              reduceVariablesToCssVariables(
                fonts,
                variableCollections,
                slugMap
              ),
              new Map()
            )
        )
      )
    )
);

/**
 * INTERNAL
 *
 * Gets a mapping of `AppliedMixinKey` to `Set<WebflowCSSRule>`, which is useful
 * when we need to sync any updates to our CSS variables to rules that apply a
 * mode whose variable values are changing.
 */
function getMixinKeyToCssRulesMap(
  variableCollections: StyleVariables.VariableCollectionMap,
  variableValueMap: VariableValueMap,
  nativeSheet: CSSStyleSheet,
  slugMap: Plugins.PluginsType['slugMap'],
  isOpenCanvasActive: boolean
): Map<
  StyleVariables.VariableCollectionId,
  Map<StyleVariables.VariableModeId, Set<WebflowCSSRule>>
> {
  function getConditionalRules() {
    return isOpenCanvasActive
      ? getNativeContainerQueriesForOpenCanvas(nativeSheet)
      : getNativeMediaRules(nativeSheet);
  }

  const result = new Map<
    StyleVariables.VariableCollectionId,
    Map<StyleVariables.VariableModeId, Set<WebflowCSSRule>>
  >();

  const rootBlock = isOpenCanvasActive
    ? ensureOpenCanvasFrameVariableBlock(nativeSheet)
    : ensureRootVariableBlock(nativeSheet);

  // We always add the root variable block rule since it always contains the
  // base variable values for every collection:
  for (const [collectionId] of variableCollections) {
    const collectionRules: Map<string, Set<WebflowCSSRule>> = result.get(
      collectionId
    ) ?? new Map();
    const baseModeRules: Set<WebflowCSSRule> =
      collectionRules.get(StyleVariables.BASE_VARIABLE_MODE_ID) ?? new Set();
    baseModeRules.add(rootBlock);
    collectionRules.set(StyleVariables.BASE_VARIABLE_MODE_ID, baseModeRules);
    result.set(collectionId, collectionRules);

    if (getFeatureFlagOutsideOfReact(FEATURE_FLAGS.CAMPAIGN_BUILD_MODE)) {
      for (const modeId of [
        StyleVariables.INHERIT_VARIABLE_MODE_ID,
        StyleVariables.BASE_VARIABLE_MODE_ID,
        ...Object.keys(variableCollections.get(collectionId)?.modes ?? {}),
      ]) {
        const modePropDataAttributeBlock = ensureModePropDataAttributeBlock(
          nativeSheet,
          variableValueMap,
          collectionId,
          variableCollections,
          modeId,
          slugMap,
          isOpenCanvasActive
        );

        if (modePropDataAttributeBlock == null) continue;

        const modeRules: Set<WebflowCSSRule> =
          collectionRules.get(modeId) ?? new Set();
        modeRules.add(modePropDataAttributeBlock);
        collectionRules.set(modeId, modeRules);
      }
    }
  }

  for (const conditionalRule of getConditionalRules()) {
    if (!conditionalRule.__wf_rules) {
      continue;
    }

    for (const nativeStyleRule of Object.values(conditionalRule.__wf_rules)) {
      if (!nativeStyleRule.__wf_mixins) {
        continue;
      }

      for (const mixinKey of nativeStyleRule.__wf_mixins) {
        const parsed = getCollectionIdAndModeIdFromAppliedMixinKey(mixinKey);
        if (parsed == null) {
          continue;
        }
        const {collectionId, modeId} = parsed;

        const collectionRules = result.get(collectionId) ?? new Map();
        const modeRules = collectionRules.get(modeId) ?? new Set();
        modeRules.add(nativeStyleRule);
        collectionRules.set(modeId, modeRules);
        result.set(collectionId, collectionRules);
      }
    }
  }
  return result;
}

/**
 * INTERNAL
 *
 * Given an `AppliedMixinKey`, attempt to parse it out into a variable
 * collection ID and a variable mode ID.
 *
 * NOTE: As of August 2025, variable mode applications are the only kind of
 * mixins we use, so at the time of writing, we expect this function to work for
 * any `AppliedMixinKey` that gets provided here.
 *
 * In the future, we may encounter non-variable mixins, in which case this will
 * not return
 */
function getCollectionIdAndModeIdFromAppliedMixinKey(
  appliedMixinKey: AppliedMixinKey
) {
  const [propertyName, modeId] = appliedMixinKey.split(':');
  if (propertyName == null || modeId == null) return null;
  const collectionId = parseCollectionIdFromPropertyName(propertyName);
  if (collectionId == null) return null;
  return {collectionId, modeId};
}

/**
 * Syncs CssVariablesStore.variables changes to the stylesheet via CSSOM.
 * @param  {CssStyleSheet}  styleSheet  The native stylesheet
 * @param  {VariableMap}    oldVariables The old map of variables.
 * @param  {VariableMap}    newVariables The new map of variables.
 */
export function syncVariablesToCssVariables(
  styleSheet: CSSStyleSheet,
  {
    oldVariables = StyleVariables.emptyVariableMap,
    newVariables = StyleVariables.emptyVariableMap,
    oldVariableCollections,
    newVariableCollections,
    fonts,
    oldSlugMap,
    newSlugMap,
    isOpenCanvasActive = false,
  }: {
    oldVariables?: StyleVariables.VariableMap;
    newVariables?: StyleVariables.VariableMap;
    oldVariableCollections: StyleVariables.VariableCollectionMap;
    newVariableCollections: StyleVariables.VariableCollectionMap;
    fonts: Font[];
    oldSlugMap: Plugins.PluginsType['slugMap'];
    newSlugMap: Plugins.PluginsType['slugMap'];
    isOpenCanvasActive?: boolean;
  }
) {
  // Performance note:
  //
  // It's important that we call `renderVariablesToMap` with `oldVariables`
  // *first*, in order to make use of the cached value from the previous call to
  // this function, since we only keep a single previous result around for
  // memory efficiency via `singleMemo`.
  //
  // The call with `newVariables` then replaces the cache for the next update.
  const oldVariableValueMap = renderVariablesToMap(oldSlugMap)(
    oldVariableCollections
  )(fonts)(oldVariables);
  const newVariableValueMap = renderVariablesToMap(newSlugMap)(
    newVariableCollections
  )(fonts)(newVariables);

  // With that addition of Variable Modes, it's possible for variables to be
  // reapplied in any style rule, so we need to update any style rules which
  // apply a mode whose values have changed.
  //
  // To do this efficiently, we first need a mapping of `AppliedMixinKey` to
  // Set<WebflowCSSRule>

  const appliedMixinKeyToCssRules = getMixinKeyToCssRulesMap(
    newVariableCollections,
    newVariableValueMap,
    styleSheet,
    newSlugMap,
    isOpenCanvasActive
  );

  for (const [collectionId, modeIdToCssRules] of appliedMixinKeyToCssRules) {
    for (const [modeId, cssRules] of modeIdToCssRules) {
      // If there are no rules that apply this mode, then we can skip it:
      if (cssRules.size === 0) continue;

      const oldValues =
        oldVariableValueMap.get(collectionId)?.get(modeId) ??
        new Map<string, string>();
      const newValues =
        newVariableValueMap.get(collectionId)?.get(modeId) ??
        new Map<string, string>();

      // We need to rewrite viewport units in the variable values in open canvas
      // since the meaning of viewport units changes when working with frames

      const oldValuesRewritten = rewriteValueMapIfNeeded(
        oldValues,
        isOpenCanvasActive
      );
      const newValuesRewritten = rewriteValueMapIfNeeded(
        newValues,
        isOpenCanvasActive
      );

      for (const [name, value] of newValuesRewritten) {
        if (
          !oldValuesRewritten.has(name) ||
          oldValuesRewritten.get(name) !== value
        ) {
          for (const cssRule of cssRules) {
            const priority = isModeDataAttributeSelector(cssRule.selectorText)
              ? 'important'
              : '';
            setCssVariable(cssRule, name, value, priority);
          }
        }
      }
      for (const [name] of oldValues) {
        if (!newValues.has(name)) {
          for (const cssRule of cssRules) {
            removeCssVariable(cssRule, name);
          }
        }
      }
    }
  }
}

function updateSingleRuleWithAudit(
  nativeRule: WebflowCSSRule,
  selectorText: string | null | undefined,
  styleText: string,
  seenMixins: Set<AppliedMixinKey>,
  auditActions?: Array<{
    type: string;
    guid?: string;
    updateFlags?: number;
    after?: string | null | undefined;
  }> | null,
  isElementStyle: boolean = false
) {
  const updateFlags = updateStyleRule(nativeRule, {
    selectorText,
    styleText,
    seenMixins,
    isElementStyle,
  });

  if (auditActions && updateFlags !== NO_CHANGE) {
    auditActions.push({
      type: 'edit',
      guid: nativeRule.__wf_guid,
      updateFlags,
    });
  }
}

/**
 * Converts a native CSSOM stylesheet to an object that's easy to inspect and compare.
 * @param  {CSSStyleSheet} nativeSheet  The native CSSOM stylesheet.
 * @return {Object}                     An object that describes the current state of the stylesheet.
 */
function serializeStyleSheet(nativeSheet: CSSStyleSheet) {
  return getNativeMediaRules(nativeSheet).map((nativeMediaRule) => {
    return {
      guid: nativeMediaRule.__wf_guid,
      // @ts-expect-error - TS2339 - Property 'media' does not exist on type 'CSSRule'.
      mediaText: nativeMediaRule.media.mediaText,
      rules: _.map(nativeMediaRule.cssRules, (nativeStyleRule) => {
        return {
          guid: nativeStyleRule.__wf_guid,
          sel: nativeStyleRule.selectorText,
          css: nativeStyleRule.style.cssText,
        };
      }),
    };
  });
}

function validateStyleText(styleText?: string) {
  if (!_.isString(styleText)) {
    throw new Error('Invalid styleText!');
  }
}

export const forTestSuite = {
  BOTH_CHANGED,
  NO_CHANGE,
  STYLE_CHANGED,
  TEXT_CHANGED,
  createContainerQuery,
  createMediaQuery,
  createStyleRule,
  ensureMediaQueries,
  ensureModePropDataAttributeBlock,
  ensureOpenCanvasFrameVariableBlock,
  ensureRootVariableBlock,
  generateRuleMap,
  getNativeMediaRules,
  getNativeContainerQueriesForOpenCanvas,
  getSelectorForOpenCanvas,
  filterOnlyTypographicStyles,
  rewriteValueMapIfNeeded,
  maybeRewriteViewportUnits,
  removeRootCssVariable,
  serializeStyleSheet,
  setRootCssVariable,
  syncStyleSheet,
  syncSwatchesToCssVariables,
  syncVariablesToCssVariables,
  makeAllImportant,
};
